//@flow
import React, {useEffect, useMemo} from 'react';
import {Link, useHistory, useParams} from 'react-router-dom';

import {makeStyles, Typography, IconButton, Button} from '@material-ui/core';
import {colors} from '../../../../../../assets/styles/colors';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import {useForm} from 'react-hook-form';
import CustomTextField from '../../../../../../elements/CustomTextField/CustomTextField';

import InlineLoader from '../../../../../../elements/LoadingSpinner/InlineLoader';
import {ArrowBack} from '@material-ui/icons';
import {
  useStoreInformationBlockActions,
  useStoreInformationBlock,
  useStoreInformationBlocks,
} from '../../../../utils/information-block';
import {INFORMATION_BLOCK_LAYOUT_TYPE} from './constants';

import option1Image from '../../../../../../assets/img/store-information/store_information_block_option_1.webp';
import option2Image from '../../../../../../assets/img/store-information/store_information_block_option_2.webp';
import option3Image from '../../../../../../assets/img/store-information/store_information_block_option_3.webp';
import InformationBlockContent from './InformationBlockContent';
import {useHomePageLayoutActions} from '../../../../../page-layout/utils/home-page';
import {HOME_PAGE_SECTION_NAMES} from '../../../../utils/home-page-sections/constants';
import {TiendadaSwitch} from '../../../../../../elements/tiendada-switch';

const TITLE_MAX_LENGTH = 80;
const SUBTITLE_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 88;

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    maxWidth: '1200px',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,.8)',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
  },
  header: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.75rem',
    paddingBottom: '1rem',
    borderBottom: `1px solid ${colors.grey200}`,

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 0,
    },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: colors.grey100,
    borderRadius: '8px',
    width: '100%',

    [theme.breakpoints.up('sm')]: {
      width: 'auto',
      padding: '0.25rem 0.75rem',
    },
  },
  switchLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey700,
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: colors.grey900,
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',
  },
  backButton: {
    background: 'white',
    padding: '6px',
    marginRight: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.grey800,
    marginBottom: '0.25rem',
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
    padding: '1rem',
    backgroundColor: '#FAFAFA',
    borderRadius: '12px',
    border: `1px solid ${colors.grey200}`,
  },
  leftContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    gap: '1.25rem',
    position: 'relative',
    padding: '1.25rem',
    borderRadius: '16px',
    background: 'white',
    boxShadow:
      '0px 1px 3px 0px rgba(96, 97, 112, 0.12), 0px 0px 1px 0px rgba(40, 41, 61, 0.08)',
  },
  rightContent: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      height: 'min-content',
      position: 'sticky',
      top: '5.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
    },
  },
  submitButton: {
    display: 'block',
    maxWidth: '200px',
    padding: '0.75rem 2rem',
  },
  captionText: {
    fontSize: '0.875rem',
    color: colors.grey600,
    marginBottom: '.25rem',
    fontWeight: 500,
  },
  items: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '.75rem',
  },
  itemButton: {
    padding: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  item: {
    width: '100%',
    height: '100%',
    background: 'white',
    borderRadius: '12px',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '.75rem',
    cursor: 'pointer',
    position: 'relative',
    border: `2px solid ${colors.grey200}`,
    transition: 'all 0.2s ease',

    '&:hover': {
      borderColor: colors.primary,
      backgroundColor: 'rgba(103, 58, 183, 0.02)',
    },

    '&[data-selected=true]': {
      borderColor: colors.primary,
      backgroundColor: 'rgba(103, 58, 183, 0.04)',
    },
  },
  itemLabel: {
    display: 'block',
    textAlign: 'center',
    color: colors.grey700,
    fontSize: '.75rem',
    fontWeight: 500,
  },
  layoutThumbnail: {
    display: 'flex',
    alignItems: 'center',
    height: '70px',
    width: 'auto',
    objectFit: 'contain',
  },
  checkbox: {
    position: 'absolute',
    top: '.5rem',
    left: '.5rem',
    width: '1.25rem',
    height: '1.25rem',
    background: '#fff',
    borderRadius: '50%',
    border: `2px solid ${colors.grey300}`,
    transition: 'all 0.2s ease',

    '&[data-checked=true]': {
      border: `6px solid ${colors.primary}`,
    },
  },
  fieldWrapper: {
    position: 'relative',
  },
  charCounter: {
    position: 'absolute',
    right: '12px',
    bottom: '10px',
    fontSize: '11px',
    color: colors.grey500,
    pointerEvents: 'none',
    backgroundColor: 'white',
    padding: '0 4px',

    '&[data-warning="true"]': {
      color: colors.warning,
    },
    '&[data-error="true"]': {
      color: colors.error,
    },
  },
  descriptionCharCounter: {
    position: 'absolute',
    right: '12px',
    bottom: '12px',
    fontSize: '11px',
    color: colors.grey500,
    pointerEvents: 'none',
    backgroundColor: 'white',
    padding: '0 4px',

    '&[data-warning="true"]': {
      color: colors.warning,
    },
    '&[data-error="true"]': {
      color: colors.error,
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.grey200}`,
  },
}));

const FORM_FIELDS = {
  TITLE: 'title',
  SUBTITLE: 'subtitle',
  DESCRIPTION: 'description',
  LAYOUT: 'layout',
  ENABLED: 'enabled',
  CONTENT_LIST: 'contentList',
};

type Layout = {
  name: string,
  imageSrc: string,
  label: string,
};

const LAYOUTS: Layout[] = [
  {
    name: INFORMATION_BLOCK_LAYOUT_TYPE.TEXT_IMAGE,
    imageSrc: option1Image,
    label: 'Opción 1 - Texto y descripción',
  },
  {
    name: INFORMATION_BLOCK_LAYOUT_TYPE.TEXT_LIST,
    imageSrc: option2Image,
    label: 'Opción 2 - Texto y contenido',
  },
  {
    name: INFORMATION_BLOCK_LAYOUT_TYPE.COLLAPSIBLE,
    imageSrc: option3Image,
    label: 'Opción 3 - Texto y lista',
  },
];

export default function InformationBlockForm() {
  const classes = useStyles();
  const {register, errors, handleSubmit, watch, reset, setValue} = useForm();

  const {loading} = useStoreInformationBlockActions();

  const {refreshBlocks} = useStoreInformationBlocks();

  const history = useHistory();
  const {id} = useParams();

  const isEditing = id !== 'new';

  const {addSection} = useHomePageLayoutActions();

  const {block} = useStoreInformationBlock(isEditing ? id : undefined);

  const {
    createStoreInformationBlock,
    updateStoreInformationBlock,
    loading: updating,
  } = useStoreInformationBlockActions();

  const onSubmit = data => {
    if (isEditing && block) {
      updateStoreInformationBlock(block.storeInformationBlockId, {
        enabled: !!data[FORM_FIELDS.ENABLED],
        storeInformationBlockType: data[FORM_FIELDS.LAYOUT] || undefined,
        title: data[FORM_FIELDS.TITLE] || undefined,
        subtitle: data[FORM_FIELDS.SUBTITLE] || undefined,
        description: data[FORM_FIELDS.DESCRIPTION] || undefined,
        content: data[FORM_FIELDS.CONTENT_LIST].map(item => ({
          title: item.title,
          description: item.text,
          file: item.image || undefined,
          imageId: item.imageId || undefined,
        })),
      }).then(result => {
        if (!result) return;

        refreshBlocks();
        history.push('/my-store/pages/home-page');
      });

      return;
    }

    createStoreInformationBlock({
      enabled: true,
      storeInformationBlockType: data[FORM_FIELDS.LAYOUT],
      title: data[FORM_FIELDS.TITLE],
      subtitle: data[FORM_FIELDS.SUBTITLE],
      description: data[FORM_FIELDS.DESCRIPTION],
      content: data[FORM_FIELDS.CONTENT_LIST].map(item => ({
        title: item.title,
        description: item.text,
        file: item.image,
      })),
    }).then(result => {
      if (!result) return;

      addSection({
        enabled: true,
        pageSectionType: HOME_PAGE_SECTION_NAMES.STORE_INFORMATION_BLOCK,
        sectionIdentifier: result.storeInformationBlockId,
      });
      refreshBlocks();
      history.push('/my-store/pages/home-page');
    });
  };

  useEffect(() => {
    register(FORM_FIELDS.ENABLED);
    register(FORM_FIELDS.LAYOUT, {
      required: 'Campo requerido',
    });

    register(FORM_FIELDS.CONTENT_LIST, {
      validate: value => {
        if (!value || !value.length)
          return 'Debes agregar al menos un contenido';

        const hasInvalid = value.some(
          item => !item.title || !item.text || (!item.imageId && !item.image)
        );
        return hasInvalid
          ? 'Todos los campos (imagen, título y texto) son requeridos'
          : true;
      },
    });

    register(FORM_FIELDS.DESCRIPTION, {
      maxLength: {
        value: 88,
        message: 'Máximo 88 caracteres',
      },
    });
  }, [register]);

  useEffect(() => {
    if (!block) return;

    setValue(FORM_FIELDS.ENABLED, !!block.enabled);
    setValue(FORM_FIELDS.LAYOUT, block.storeInformationBlockType);
    setValue(FORM_FIELDS.TITLE, block.title);
    setValue(FORM_FIELDS.SUBTITLE, block.subtitle);

    setValue(FORM_FIELDS.DESCRIPTION, block.description);

    setValue(
      FORM_FIELDS.CONTENT_LIST,
      block.content.map((item, index) => ({
        title: item.title,
        text: item.description,
        imageId: item.imageId || undefined,
        id: item.title + index,
      }))
    );
  }, [block, reset, setValue]);

  const enabled = watch(FORM_FIELDS.ENABLED);
  const layoutSelected = watch(FORM_FIELDS.LAYOUT);
  const description = watch(FORM_FIELDS.DESCRIPTION) || '';
  const title = watch(FORM_FIELDS.TITLE) || '';
  const subtitle = watch(FORM_FIELDS.SUBTITLE) || '';

  const getCharCounterStatus = (current: number, max: number) => {
    const remaining = max - current;
    if (remaining <= 0) return 'error';
    if (remaining <= 10) return 'warning';
    return 'normal';
  };

  const titleStatus = getCharCounterStatus(title.length, TITLE_MAX_LENGTH);
  const subtitleStatus = getCharCounterStatus(
    subtitle.length,
    SUBTITLE_MAX_LENGTH
  );
  const descriptionStatus = getCharCounterStatus(
    description.length,
    DESCRIPTION_MAX_LENGTH
  );

  const initialContent = useMemo(() => {
    if (!block) return;
    return block.content.map((item, index) => ({
      title: item.title,
      text: item.description,
      imageId: item.imageId || undefined,
      id: item.title + index,
    }));
  }, [block]);

  return (
    <form className={classes.container} onSubmit={handleSubmit(onSubmit)}>
      {(loading || updating) && (
        <div className={classes.loader}>
          <InlineLoader size={40} />
        </div>
      )}

      <div className={classes.leftContent}>
        <header className={classes.header}>
          <div className={classes.headerLeft}>
            <IconButton
              className={classes.backButton}
              component={Link}
              to="/my-store/pages/home-page"
            >
              <ArrowBack />
            </IconButton>
            <Typography className={classes.title}>
              Crea una sección informacional
            </Typography>
          </div>
          {isEditing && (
            <div className={classes.switchContainer}>
              <span className={classes.switchLabel}>
                {enabled ? 'Habilitado' : 'Deshabilitado'}
              </span>
              <TiendadaSwitch
                checked={!!enabled}
                onChange={() => {
                  setValue(FORM_FIELDS.ENABLED, !enabled);
                }}
                name={FORM_FIELDS.ENABLED}
              />
            </div>
          )}
        </header>

        <section className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            1. Forma de la sección informacional
          </Typography>
          <div className={classes.items}>
            {LAYOUTS.map(layout => (
              <Button
                key={layout.name}
                className={classes.itemButton}
                onClick={() => {
                  setValue(FORM_FIELDS.LAYOUT, layout.name);
                }}
              >
                <div
                  className={classes.item}
                  data-selected={layoutSelected === layout.name}
                >
                  <div
                    className={classes.checkbox}
                    data-checked={layoutSelected === layout.name}
                  />
                  <img
                    src={layout.imageSrc}
                    alt="Option"
                    className={classes.layoutThumbnail}
                  />
                  <span className={classes.itemLabel}>{layout.label}</span>
                </div>
              </Button>
            ))}
          </div>
        </section>

        <section className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            2. Encabezado y textos
          </Typography>

          <div className={classes.fieldWrapper}>
            <CustomTextField
              label=""
              name={FORM_FIELDS.TITLE}
              placeholder="Título de encabezado de ceja"
              inputRef={register({
                maxLength: {
                  value: TITLE_MAX_LENGTH,
                  message: `Máximo ${TITLE_MAX_LENGTH} caracteres`,
                },
                required: 'Campo requerido',
              })}
              inputProps={{maxLength: TITLE_MAX_LENGTH}}
              error={errors[FORM_FIELDS.TITLE]}
              helperText={
                errors[FORM_FIELDS.TITLE] && errors[FORM_FIELDS.TITLE].message
              }
            />
            <span
              className={classes.charCounter}
              data-warning={titleStatus === 'warning'}
              data-error={titleStatus === 'error'}
            >
              {title.length}/{TITLE_MAX_LENGTH}
            </span>
          </div>

          <div className={classes.fieldWrapper}>
            <CustomTextField
              label=""
              name={FORM_FIELDS.SUBTITLE}
              placeholder="Subtítulo"
              inputRef={register({
                maxLength: {
                  value: SUBTITLE_MAX_LENGTH,
                  message: `Máximo ${SUBTITLE_MAX_LENGTH} caracteres`,
                },
                required: 'Campo requerido',
              })}
              inputProps={{maxLength: SUBTITLE_MAX_LENGTH}}
              error={errors[FORM_FIELDS.SUBTITLE]}
              helperText={
                errors[FORM_FIELDS.SUBTITLE] &&
                errors[FORM_FIELDS.SUBTITLE].message
              }
            />
            <span
              className={classes.charCounter}
              data-warning={subtitleStatus === 'warning'}
              data-error={subtitleStatus === 'error'}
            >
              {subtitle.length}/{SUBTITLE_MAX_LENGTH}
            </span>
          </div>

          {layoutSelected === INFORMATION_BLOCK_LAYOUT_TYPE.TEXT_IMAGE && (
            <div className={classes.fieldWrapper}>
              <CustomTextField
                label=""
                multiline
                rows={6}
                value={description}
                onChange={e => {
                  setValue(FORM_FIELDS.DESCRIPTION, e.target.value);
                }}
                name={FORM_FIELDS.DESCRIPTION}
                placeholder="Texto informacional"
                inputProps={{maxLength: DESCRIPTION_MAX_LENGTH}}
                error={errors[FORM_FIELDS.DESCRIPTION]}
                helperText={
                  errors[FORM_FIELDS.DESCRIPTION] &&
                  errors[FORM_FIELDS.DESCRIPTION].message
                }
              />
              <span
                className={classes.descriptionCharCounter}
                data-warning={descriptionStatus === 'warning'}
                data-error={descriptionStatus === 'error'}
              >
                {description.length}/{DESCRIPTION_MAX_LENGTH}
              </span>
            </div>
          )}
        </section>

        <section className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            3. Contenidos con imágenes
          </Typography>

          {errors[FORM_FIELDS.CONTENT_LIST] && (
            <Typography color="error" variant="body2">
              {errors[FORM_FIELDS.CONTENT_LIST].message}
            </Typography>
          )}

          {initialContent ? (
            <InformationBlockContent
              onChange={data => {
                setValue(FORM_FIELDS.CONTENT_LIST, data, {
                  shouldValidate: true,
                });
              }}
              initialContent={initialContent}
            />
          ) : (
            <InformationBlockContent
              onChange={data => {
                setValue(FORM_FIELDS.CONTENT_LIST, data, {
                  shouldValidate: true,
                });
              }}
            />
          )}
        </section>

        <footer className={classes.actions}>
          <CustomButton
            className={classes.submitButton}
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
          >
            Guardar
          </CustomButton>
        </footer>
      </div>
    </form>
  );
}
